<template>
    <div>
        <h2 class="primary darken-2 white--text font-weight-bold subheading py-2 text-xs-center">
            CAMPAIGN MANAGEMENT & MIGRATION
        </h2>
        <v-divider></v-divider>

        <v-card class="pa-3 ma-2">
            <!-- Migration Status Overview -->
            <v-card color="blue lighten-5" class="mb-4 pa-3">
                <v-layout row wrap align-center>
                    <v-flex xs12 md8>
                        <h3 class="title mb-2">
                            <v-icon large color="blue">sync</v-icon>
                            AI Analysis Migration System
                        </h3>
                        <p class="body-1 mb-0">
                            This system analyzes old campaigns with AI and integrates them into the new analytics platform.
                        </p>
                    </v-flex>
                    <v-flex xs12 md4 class="text-xs-center">
                        <v-btn
                            @click="loadMigrationStatus"
                            color="blue"
                            dark
                            :loading="loadingStatus"
                        >
                            <v-icon left>refresh</v-icon>
                            Check Status
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-card>

            <!-- Migration Status Cards -->
            <v-layout row wrap v-if="migrationStatus" class="mb-4">
                <v-flex xs12 sm6 md3 pa-2>
                    <v-card color="teal lighten-5" class="pa-3 text-xs-center">
                        <v-icon size="50" color="teal">folder</v-icon>
                        <h2 class="display-1 teal--text">{{ migrationStatus.summary.total_campaigns }}</h2>
                        <p class="caption mb-0">Total Campaigns</p>
                    </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 pa-2>
                    <v-card color="green lighten-5" class="pa-3 text-xs-center">
                        <v-icon size="50" color="green">check_circle</v-icon>
                        <h2 class="display-1 green--text">{{ migrationStatus.summary.already_migrated }}</h2>
                        <p class="caption mb-0">Already Migrated</p>
                    </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 pa-2>
                    <v-card color="orange lighten-5" class="pa-3 text-xs-center">
                        <v-icon size="50" color="orange">pending</v-icon>
                        <h2 class="display-1 orange--text">{{ migrationStatus.summary.needs_migration }}</h2>
                        <p class="caption mb-0">Needs Migration</p>
                    </v-card>
                </v-flex>
                <v-flex xs12 sm6 md3 pa-2>
                    <v-card color="blue lighten-5" class="pa-3 text-xs-center">
                        <v-icon size="50" color="blue">trending_up</v-icon>
                        <h2 class="display-1 blue--text">{{ migrationStatus.summary.migration_progress }}%</h2>
                        <p class="caption mb-0">Progress</p>
                    </v-card>
                </v-flex>
            </v-layout>

            <!-- Progress Bar -->
            <v-card v-if="migrationStatus" class="mb-4">
                <v-card-text>
                    <h4 class="subheading mb-2">Migration Progress</h4>
                    <v-progress-linear
                        :value="migrationStatus.summary.migration_progress"
                        color="teal"
                        height="25"
                    >
                        <strong>{{ migrationStatus.summary.migration_progress }}% Complete</strong>
                    </v-progress-linear>
                </v-card-text>
            </v-card>

            <!-- Migration Actions -->
            <v-card class="mb-4">
                <v-card-title class="teal white--text">
                    <v-icon left dark>play_arrow</v-icon>
                    Migration Actions
                </v-card-title>
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12 md6 pa-2>
                            <v-select
                                v-model="batchSize"
                                :items="[10, 25, 50, 100, 200]"
                                label="Batch Size (campaigns per run)"
                                color="teal"
                                hint="Number of campaigns to process at once"
                                persistent-hint
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 md6 pa-2>
                            <v-checkbox
                                v-model="forceReanalyze"
                                label="Force Re-analyze Already Migrated Campaigns"
                                color="teal"
                                hint="Re-run AI analysis on campaigns that were already migrated"
                                persistent-hint
                            ></v-checkbox>
                        </v-flex>
                    </v-layout>

                    <v-divider class="my-3"></v-divider>

                    <v-layout row wrap justify-center>
                        <v-flex xs12 sm6 md4 pa-2>
                            <v-btn
                                @click="startMigration"
                                color="teal"
                                dark
                                large
                                block
                                :loading="migrating"
                                :disabled="migrationStatus && migrationStatus.summary.needs_migration === 0 && !forceReanalyze"
                            >
                                <v-icon left>cloud_upload</v-icon>
                                Start Migration
                            </v-btn>
                        </v-flex>
                        <v-flex xs12 sm6 md4 pa-2>
                            <v-btn
                                @click="startFullMigration"
                                color="orange darken-2"
                                dark
                                large
                                block
                                :loading="migrating"
                            >
                                <v-icon left>autorenew</v-icon>
                                Migrate All (Auto)
                            </v-btn>
                        </v-flex>
                    </v-layout>

                    <!-- Migration Results -->
                    <v-alert
                        v-if="migrationResults"
                        :type="migrationResults.status === 'success' ? 'success' : 'warning'"
                        :value="true"
                        class="mt-3"
                    >
                        <h4 class="subheading">{{ migrationResults.message }}</h4>
                        <p class="mb-0">
                            <strong>Processed:</strong> {{ migrationResults.results.processed }} |
                            <strong>Success:</strong> {{ migrationResults.results.success }} |
                            <strong>Failed:</strong> {{ migrationResults.results.failed }}
                        </p>
                        <p v-if="migrationResults.remaining > 0" class="mb-0">
                            <strong>{{ migrationResults.remaining }}</strong> campaigns remaining
                        </p>

                        <!-- Sample Results -->
                        <div v-if="migrationResults.results.samples.length > 0" class="mt-2">
                            <p class="caption mb-1"><strong>Sample Analyzed:</strong></p>
                            <v-chip
                                v-for="sample in migrationResults.results.samples"
                                :key="sample.campaign_id"
                                color="white"
                                small
                                class="ma-1"
                            >
                                <strong>{{ sample.campaign_name }}</strong>
                                <v-icon right small :color="getCategoryColor(sample.category)">label</v-icon>
                            </v-chip>
                        </div>

                        <!-- Errors -->
                        <div v-if="migrationResults.results.errors.length > 0" class="mt-2">
                            <p class="caption mb-1 red--text"><strong>Errors:</strong></p>
                            <ul>
                                <li v-for="error in migrationResults.results.errors" :key="error.campaign_id">
                                    {{ error.campaign_name }}: {{ error.error }}
                                </li>
                            </ul>
                        </div>
                    </v-alert>
                </v-card-text>
            </v-card>

            <!-- Breakdown by Status & Type -->
            <v-layout row wrap v-if="migrationStatus" class="mb-4">
                <v-flex xs12 md6 pa-2>
                    <v-card>
                        <v-card-title class="subheading">Campaigns by Status</v-card-title>
                        <v-card-text>
                            <div
                                v-for="(count, status) in migrationStatus.breakdown.by_status"
                                :key="'status-'+status"
                                class="mb-2"
                            >
                                <div class="caption">{{ status }}</div>
                                <v-progress-linear
                                    :value="(count / migrationStatus.summary.needs_migration) * 100"
                                    color="blue"
                                    height="20"
                                >
                                    {{ count }}
                                </v-progress-linear>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-flex>
                <v-flex xs12 md6 pa-2>
                    <v-card>
                        <v-card-title class="subheading">Campaigns by Type</v-card-title>
                        <v-card-text>
                            <div
                                v-for="(count, type) in migrationStatus.breakdown.by_type"
                                :key="'type-'+type"
                                class="mb-2"
                            >
                                <div class="caption">{{ type }}</div>
                                <v-progress-linear
                                    :value="(count / migrationStatus.summary.needs_migration) * 100"
                                    color="purple"
                                    height="20"
                                >
                                    {{ count }}
                                </v-progress-linear>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>

            <!-- Sample Campaigns Preview -->
            <v-card v-if="migrationStatus && migrationStatus.sample_campaigns.length > 0" class="mb-4">
                <v-card-title class="subheading">
                    <v-icon left>preview</v-icon>
                    Sample Campaigns Needing Migration
                </v-card-title>
                <v-data-table
                    :headers="sampleHeaders"
                    :items="migrationStatus.sample_campaigns"
                    hide-actions
                >
                    <template v-slot:items="props">
                        <td>{{ props.item.name }}</td>
                        <td>{{ props.item.user }}</td>
                        <td>{{ props.item.status }}</td>
                        <td>{{ props.item.type }}</td>
                        <td>
                            <v-btn
                                small
                                flat
                                color="teal"
                                @click="analyzeSingle(props.item.id)"
                                :loading="analyzingId === props.item.id"
                            >
                                <v-icon small>psychology</v-icon>
                                Analyze Now
                            </v-btn>
                        </td>
                    </template>
                </v-data-table>
            </v-card>

            <!-- All Campaigns View -->
            <v-card>
                <v-card-title class="subheading">
                    <v-icon left>list</v-icon>
                    All Campaigns with Analysis Status
                    <v-spacer></v-spacer>
                    <v-btn @click="loadAllCampaigns" color="teal" small dark :loading="loadingCampaigns">
                        <v-icon left small>refresh</v-icon>
                        Load
                    </v-btn>
                </v-card-title>

                <!-- Filters -->
                <v-card-text v-if="allCampaigns">
                    <v-layout row wrap>
                        <v-flex xs12 md3 pa-2>
                            <v-select
                                v-model="campaignFilters.status"
                                :items="statusOptions"
                                label="Status Filter"
                                clearable
                                color="teal"
                                @change="loadAllCampaigns"
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 md3 pa-2>
                            <v-select
                                v-model="campaignFilters.has_intent"
                                :items="[
                                    {text: 'All', value: null},
                                    {text: 'Has Analysis', value: 'true'},
                                    {text: 'No Analysis', value: 'false'}
                                ]"
                                label="Analysis Status"
                                color="teal"
                                @change="loadAllCampaigns"
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 md3 pa-2>
                            <v-select
                                v-model="campaignFilters.category"
                                :items="categories"
                                label="Category Filter"
                                clearable
                                color="teal"
                                @change="loadAllCampaigns"
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 md3 pa-2>
                            <v-text-field
                                v-model="search"
                                append-icon="search"
                                label="Search"
                                single-line
                                color="teal"
                            ></v-text-field>
                        </v-flex>
                    </v-layout>
                </v-card-text>

                <!-- Campaigns Table -->
                <v-data-table
                    v-if="allCampaigns"
                    :headers="campaignHeaders"
                    :items="allCampaigns.campaigns"
                    :search="search"
                    :rows-per-page-items="[10, 25, 50, 100]"
                >
                    <template v-slot:items="props">
                        <td>{{ props.item.name }}</td>
                        <td>{{ props.item.user }}</td>
                        <td>{{ props.item.status }}</td>
                        <td>
                            <v-icon
                                :color="props.item.has_analysis ? 'green' : 'grey'"
                                small
                            >
                                {{ props.item.has_analysis ? 'check_circle' : 'cancel' }}
                            </v-icon>
                        </td>
                        <td>
                            <v-chip
                                v-if="props.item.analysis && props.item.analysis.category"
                                small
                                :color="getCategoryColor(props.item.analysis.category)"
                                text-color="white"
                            >
                                {{ props.item.analysis.category }}
                            </v-chip>
                            <span v-else class="grey--text">N/A</span>
                        </td>
                        <td>
                            <v-chip
                                v-if="props.item.analysis && props.item.analysis.urgency"
                                small
                                :color="getUrgencyColor(props.item.analysis.urgency)"
                                text-color="white"
                            >
                                {{ props.item.analysis.urgency }}
                            </v-chip>
                            <span v-else class="grey--text">N/A</span>
                        </td>
                        <td>
                            <v-chip
                                v-for="kw in (props.item.analysis && props.item.analysis.keywords ? props.item.analysis.keywords.slice(0, 3) : [])"
                                :key="kw"
                                small
                                class="ma-1"
                            >
                                {{ kw }}
                            </v-chip>
                        </td>
                        <td>
                            <v-btn
                                small
                                icon
                                @click="viewCampaignDetail(props.item)"
                            >
                                <v-icon small>visibility</v-icon>
                            </v-btn>
                            <v-btn
                                v-if="!props.item.has_analysis"
                                small
                                icon
                                color="teal"
                                @click="analyzeSingle(props.item.id)"
                                :loading="analyzingId === props.item.id"
                            >
                                <v-icon small>psychology</v-icon>
                            </v-btn>
                        </td>
                    </template>
                </v-data-table>
            </v-card>
        </v-card>

        <!-- Campaign Detail Dialog -->
        <v-dialog v-model="detailDialog" max-width="700px">
            <v-card v-if="selectedCampaign">
                <v-card-title class="headline teal darken-2 white--text">
                    Campaign Details
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="detailDialog = false">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-3">
                    <v-layout row wrap>
                        <v-flex xs12>
                            <h3>{{ selectedCampaign.name }}</h3>
                            <p class="grey--text">{{ selectedCampaign.description }}</p>
                        </v-flex>
                        <v-flex xs6>
                            <p class="caption mb-0">User</p>
                            <p>{{ selectedCampaign.user }}</p>
                        </v-flex>
                        <v-flex xs6>
                            <p class="caption mb-0">Status</p>
                            <p>{{ selectedCampaign.status }}</p>
                        </v-flex>
                        <v-flex xs12 v-if="selectedCampaign.analysis">
                            <v-divider class="my-2"></v-divider>
                            <h4 class="subheading mb-2">AI Analysis</h4>
                            <v-layout row wrap>
                                <v-flex xs6>
                                    <p class="caption mb-0">Category</p>
                                    <v-chip small :color="getCategoryColor(selectedCampaign.analysis.category)" text-color="white">
                                        {{ selectedCampaign.analysis.category }}
                                    </v-chip>
                                </v-flex>
                                <v-flex xs6>
                                    <p class="caption mb-0">Urgency</p>
                                    <v-chip small :color="getUrgencyColor(selectedCampaign.analysis.urgency)" text-color="white">
                                        {{ selectedCampaign.analysis.urgency }}
                                    </v-chip>
                                </v-flex>
                                <v-flex xs12 v-if="selectedCampaign.analysis.keywords.length > 0">
                                    <p class="caption mb-1">Keywords</p>
                                    <v-chip
                                        v-for="kw in selectedCampaign.analysis.keywords"
                                        :key="kw"
                                        small
                                        class="ma-1"
                                    >
                                        {{ kw }}
                                    </v-chip>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            loadingStatus: false,
            migrating: false,
            loadingCampaigns: false,
            analyzingId: null,

            migrationStatus: null,
            migrationResults: null,
            batchSize: 50,
            forceReanalyze: false,

            allCampaigns: null,
            campaignFilters: {
                status: null,
                has_intent: null,
                category: null
            },
            search: '',

            statusOptions: ['Ongoing', 'Completed', 'Cancelled'],
            categories: [
                'education', 'business', 'health', 'property',
                'technology', 'lifestyle', 'emergency', 'investment', 'travel', 'other'
            ],

            sampleHeaders: [
                { text: 'Campaign', value: 'name' },
                { text: 'User', value: 'user' },
                { text: 'Status', value: 'status' },
                { text: 'Type', value: 'type' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],

            campaignHeaders: [
                { text: 'Name', value: 'name' },
                { text: 'User', value: 'user' },
                { text: 'Status', value: 'status' },
                { text: 'Has Analysis', value: 'has_analysis' },
                { text: 'Category', value: 'category' },
                { text: 'Urgency', value: 'urgency' },
                { text: 'Keywords', value: 'keywords', sortable: false },
                { text: 'Actions', value: 'actions', sortable: false }
            ],

            detailDialog: false,
            selectedCampaign: null
        }
    },

    mounted() {
        document.title = "PettyCash | Campaign Migration"
        this.loadMigrationStatus()
    },

    methods: {
        async loadMigrationStatus() {
            this.loadingStatus = true
            this.$store.commit('setIsLoading', true)

            try {
                const response = await axios.get('/api/v1/savings/campaigns/migration-status/')
                this.migrationStatus = response.data
            } catch (error) {
                this.$store.commit('setSnackBarMessage', 'Failed to load status')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            }

            this.loadingStatus = false
            this.$store.commit('setIsLoading', false)
        },

        async startMigration() {
            this.migrating = true
            this.$store.commit('setIsLoading', true)

            try {
                const response = await axios.post('/api/v1/savings/campaigns/migrate/', {
                    batch_size: this.batchSize,
                    force_reanalyze: this.forceReanalyze
                })

                this.migrationResults = response.data

                this.$store.commit('setSnackBarMessage', response.data.message)
                this.$store.commit('setSnackBarColor', 'teal darken-4')
                this.$store.commit('activateSnackBar', true)

                // Refresh status
                await this.loadMigrationStatus()
            } catch (error) {
                this.$store.commit('setSnackBarMessage', 'Migration failed')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            }

            this.migrating = false
            this.$store.commit('setIsLoading', false)
        },

        async startFullMigration() {
            if (!confirm('This will migrate ALL remaining campaigns. Continue?')) {
                return
            }

            this.migrating = true
            let totalProcessed = 0
            let hasMore = true

            while (hasMore) {
                try {
                    const response = await axios.post('/api/v1/savings/campaigns/migrate/', {
                        batch_size: this.batchSize,
                        force_reanalyze: this.forceReanalyze
                    })

                    this.migrationResults = response.data
                    totalProcessed += response.data.results.processed

                    if (response.data.remaining === 0) {
                        hasMore = false
                    }

                    // Small delay between batches
                    await new Promise(resolve => setTimeout(resolve, 1000))

                } catch (error) {
                    hasMore = false
                    this.$store.commit('setSnackBarMessage', 'Migration error')
                    this.$store.commit('setSnackBarColor', 'red darken-4')
                    this.$store.commit('activateSnackBar', true)
                }
            }

            this.$store.commit('setSnackBarMessage', `Migration complete! Processed ${totalProcessed} campaigns`)
            this.$store.commit('setSnackBarColor', 'green darken-4')
            this.$store.commit('activateSnackBar', true)

            await this.loadMigrationStatus()
            this.migrating = false
        },

        async analyzeSingle(campaignId) {
            this.analyzingId = campaignId

            try {
                const response = await axios.post('/api/v1/savings/campaigns/analyze-single/', {
                    campaign_id: campaignId
                })

                this.$store.commit('setSnackBarMessage', 'Campaign analyzed successfully!')
                this.$store.commit('setSnackBarColor', 'teal darken-4')
                this.$store.commit('activateSnackBar', true)

                // Refresh
                await this.loadMigrationStatus()
                if (this.allCampaigns) {
                    await this.loadAllCampaigns()
                }
            } catch (error) {
                this.$store.commit('setSnackBarMessage', 'Analysis failed')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            }

            this.analyzingId = null
        },

        async loadAllCampaigns() {
            this.loadingCampaigns = true

            try {
                const params = new URLSearchParams()
                if (this.campaignFilters.status) params.append('status', this.campaignFilters.status)
                if (this.campaignFilters.has_intent) params.append('has_intent', this.campaignFilters.has_intent)
                if (this.campaignFilters.category) params.append('category', this.campaignFilters.category)

                const response = await axios.get(`/api/v1/savings/campaigns/all-with-analysis/?${params}`)
                this.allCampaigns = response.data
            } catch (error) {
                this.$store.commit('setSnackBarMessage', 'Failed to load campaigns')
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            }

            this.loadingCampaigns = false
        },

        viewCampaignDetail(campaign) {
            this.selectedCampaign = campaign
            this.detailDialog = true
        },

        getCategoryColor(category) {
            const colors = {
                'education': 'blue',
                'business': 'purple',
                'health': 'red',
                'property': 'green',
                'technology': 'cyan',
                'lifestyle': 'pink',
                'emergency': 'orange',
                'investment': 'teal',
                'travel': 'indigo',
                'other': 'grey'
            }
            return colors[category] || 'grey'
        },

        getUrgencyColor(urgency) {
            const colors = {
                'critical': 'red darken-2',
                'high': 'orange',
                'medium': 'yellow darken-2',
                'low': 'green'
            }
            return colors[urgency] || 'grey'
        }
    }
}
</script>
